/**
* MerchandisingPage component
* @returns <MerchandisingPage />
*/
import TitleAndButton from "../components/TitleAndButton";
import Breadcrumbs from "../components/Breadcrumbs";
import StoreGridContainer from "../components/StoreGridContainer";
import FilterContainer from "../components/FilterContainer";
import FiltersContainer from "../components/FiltersContainer";
import BoxWithBackgroundContainer from "../components/BoxWithBackgroundContainer";
import InputRadio from "../components/InputRadio";
import InputCheckBox from "../components/InputCheckBox";
import Accordion from "../components/Accordion";
import Drawer from "../components/Drawer";
import Button from "../components/Button";
import Loader from "../components/Loader";
import LegalFooter from "../components/LegalFooter";

// Import hooks and functions from React and Redux
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useGetAllMerchandisingQuery, useGetLegalesQuery } from "../redux/apiSlice";
import { uniqueCategories } from "../scripts/helpers/uniqueReducers";
import { AnimatePresence } from "framer-motion";

const MerchandisingPage = () => {
    // Get search params from URL
    const [searchParams, setSearchParams] = useSearchParams();

    // Define initial data for filters and other components
    const data = {
        image: "",
        title: "¿Qué necesitas?",
        filters: {
            type: {
                title: "Tipo",
                options: [
                    {
                        type: "radio",
                        id: "type-all",
                        label: "Todos",
                        name: "type-all",
                        value: "all",
                        isRequired: false,
                    }
                ]
            }
        }
    }

    // Perform the query to get all merchandising items
    const {
        data: merchandisingItems,
        isLoading,
        isFetching,
        isSuccess,
        isError
    } = useGetAllMerchandisingQuery()

    // Fetch all legals using the useGetLegalesQuery hook
    const { 
        data: legals, 
        isLoading: legalsIsLoading, 
        isFetching: legalsIsFetching, 
        isSuccess: legalsIsSuccess, 
        isError: legalsIsError 
    } = useGetLegalesQuery();

    // Define state variables for drawer and selected type
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [selectedType, setSelectedType] = useState(searchParams.get("categoria") || "all"); // string. suv | sedan | coupe | all
    const [showOnlineDiscount, setShowOnlineDiscount] = useState(false);
    const [hasOnlineDiscountItems, setHasOnlineDiscountItems] = useState(false);

    useEffect(() => {
        if (!isSuccess || !merchandisingItems) {
            setHasOnlineDiscountItems(false);
            return;
        }

        const hasDiscountItems = merchandisingItems.some((item) => {
            return (
                item.price_discount !== null &&
                item.price_discount !== undefined &&
                item.price_discount !== ""
            );
        });

        setHasOnlineDiscountItems(hasDiscountItems);

        if (!hasDiscountItems) {
            setShowOnlineDiscount(false);
        }
    }, [isSuccess, merchandisingItems]);

    // Handler for changing the selected type
    const typeChangeHandler = (e) => {
        const type = e.target.value;
        setSelectedType(type);
        
        // Update URL
        const newParams = new URLSearchParams(searchParams);
        if (type && type !== "all") {
            newParams.set("categoria", type);
        } else {
            newParams.delete("categoria");
        }
        setSearchParams(newParams);
    }

    // Function to toggle the drawer's open state
    const toggleDrawer = () => {
        setIsDrawerOpen(!isDrawerOpen);
    }

    // Function to filter items by type
    const filterByType = (type, items) => {
        // console.log(`filterByType ${type}`)
        if (type === "all") {
            return items;
        } else {
            // const itemsFilteredByType = items.filter((item) => item.category.id === Number(type));
            const itemsFilteredByType = items.filter((item) => {
                return item.category === type
            });
            return itemsFilteredByType;
        }
    }

    const filterByOnlineDiscount = (showDiscount, items) => {
        if (!showDiscount) {
            return items;
        }

        return items.filter((item) => {
            return (
                item.price_discount !== null &&
                item.price_discount !== undefined &&
                item.price_discount !== ""
            );
        });
    };

    const onlineDiscountChangeHandler = (e) => {
        setShowOnlineDiscount(e.target.checked);
    };

    // Define variables for type list and filtered items
    let typeList;
    let filteredItems;

    // If the query is successful, filter the items and set the type list
    if (isSuccess) {
        filteredItems = filterByType(selectedType, merchandisingItems);
        filteredItems = filterByOnlineDiscount(showOnlineDiscount, filteredItems);

        typeList = uniqueCategories(merchandisingItems)
    }

    // Configure the Breadcrumbs properties
    const BreadcrumbsProps = {
        links: [
            {
                link: "/",
                label: "Inicio"
            },
            {
                link: location.pathname,
                label: location.pathname.slice(1)
            },
        ],
    }

    // Configure the TitleAndButton properties
    const TitleAndButtonProps = {
        title: "Queremos acompañarte <br> en cada momento",
        buttons: [
            // {
            //     config: {
            //         tag: "button",
            //         text: "Encuentra tu Lexus",
            //         color: "black",
            //     },
            // }
        ],
        backgroundColor: "white",
        extraClass: ""
    }

    const typeInputList = data.filters.type.options.map((option, i) => {
        return (
            <InputRadio {...option} key={i} isChecked={selectedType === option.value.toString()} changed={typeChangeHandler} />
        )
    })

    const typeOptionsList = typeList?.map((item, i) => {
        return (
            <InputRadio
                type="radio"
                id={`type-${item}`}
                label={item.toUpperCase()}
                name={`type-${item}`}
                value={item}
                isRequired={false}
                key={i}
                isChecked={selectedType === item}
                changed={typeChangeHandler}
            />
        )
    })

    const drawerFilters = (
        [
            {
                label: "Tipo",
                content: (
                    <>
                        {typeInputList}

                        {typeList && typeOptionsList}
                    </>
                )
            },
            ...(hasOnlineDiscountItems
                ? [
                    {
                        label: "Descuento",
                        content: (
                            <InputCheckBox
                                type="checkbox"
                                id="online-discount-merchandising-mobile"
                                name="online-discount-merchandising-mobile"
                                label="Descuento Online"
                                value="online-discount-merchandising-mobile"
                                isChecked={showOnlineDiscount}
                                changed={onlineDiscountChangeHandler}
                            />
                        )
                    }
                ]
                : [])
        ]
    )

    const drawerContent = (
        <>
            <Accordion items={drawerFilters} type={"filters"} />
        </>
    );

    return (
        <>
            {isLoading && (
                <AnimatePresence>
                    <Loader />
                </AnimatePresence>
            )}

            {isSuccess && (
                <>
                    <Breadcrumbs {...BreadcrumbsProps} />

                    <TitleAndButton {...TitleAndButtonProps} />
                    <StoreGridContainer>
                        <Button tag="link" text="Filtrar por" hasIcon={"true"} onClick={toggleDrawer} />

                        <FiltersContainer>
                            <h3>{data.title}</h3>

                            <form action="">
                                {/* Type, model and version */}
                                <fieldset className="fieldset-1">
                                    {/* Type */}
                                    <FilterContainer title={data.filters.type.title}>
                                        {typeInputList}

                                        {typeList && typeOptionsList}
                                    </FilterContainer>
                                </fieldset>

                                {hasOnlineDiscountItems && (
                                    <fieldset className="fieldset-2">
                                        <FilterContainer title={"Descuento"}>
                                            <InputCheckBox
                                                type="checkbox"
                                                id="online-discount-merchandising"
                                                name="online-discount-merchandising"
                                                label="Descuento Online"
                                                value="online-discount-merchandising"
                                                isChecked={showOnlineDiscount}
                                                changed={onlineDiscountChangeHandler}
                                            />
                                        </FilterContainer>
                                    </fieldset>
                                )}
                            </form>
                        </FiltersContainer>

                        <BoxWithBackgroundContainer items={filteredItems} extraClass="--store-version" />

                        <Drawer title={"¿Qué modelo estás buscando?"} content={drawerContent} isOpen={isDrawerOpen} close={toggleDrawer} />
                    </StoreGridContainer>
                </>
            )}

            {isError && <p>Hubo un error</p>}

            {legalsIsSuccess && <LegalFooter content={legals.merchandise}/>}
        </>
    )
}

export default MerchandisingPage
